import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withMethods } from '@ngrx/signals';
import { addEntities, addEntity, removeEntity, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { pipe, switchMap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Phase } from '../../processus-phases/models/phase.model';
import { ProcessusPhaseService } from '../processus-phase.service';
import { PhaseCreateRequest } from '../requests/create-request';
import { UpdateRequest } from '../requests/update-request';

export function avecMéthodesPhases() {
  return signalStoreFeature(
    // Updater
    withMethods((store) => ({
      chargerPhases: (state: { phases: Phase[] }) => patchState(store, setAllEntities(state.phases)),
      ajouterPhase: (state: { phase: Phase }) => patchState(store, addEntity(state.phase)),
      ajouterPhases: (state: { phases: Phase[] }) => patchState(store, addEntities(state.phases)),
      modifierPhase: (state: { phase: Phase }) =>
        patchState(store, updateEntity({ id: state.phase.id, changes: state.phase })),
      supprimerPhase: (id: string) => patchState(store, removeEntity(id)),
    })),

    // Effects
    withMethods((store, phaseService = inject(ProcessusPhaseService)) => ({
      charger: rxMethod<string>(
        pipe(
          switchMap((idRéférentiel) =>
            phaseService.findAll(idRéférentiel).pipe(
              tapResponse({
                next: (result) => store.chargerPhases({ phases: result.items }),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des phases, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      créer: rxMethod<{ data: PhaseCreateRequest; next: () => void; error: () => void }>(
        pipe(
          switchMap(({ data, next, error }) =>
            phaseService.créer(data).pipe(
              tapResponse({
                next: (phases) => {
                  store.ajouterPhases({ phases });
                  next();
                },
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la création des phases, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        idPhase: string;
        data: UpdateRequest;
        next: () => void;
        error?: () => void;
      }>(
        pipe(
          switchMap(({ idPhase, data, next, error }) =>
            phaseService.modifier(idPhase, data).pipe(
              tapResponse({
                next: () => next(),
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la modification de la phase, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error?.();
                },
              }),
            ),
          ),
        ),
      ),
      selection: rxMethod<string>(
        pipe(
          switchMap((idPhase) => phaseService.findById(idPhase)),
          tapResponse({
            next: (phase) => store.ajouterPhase({ phase }),
            error: (err) => {
              console.error(err);
              const toastrId = uuidv7();
              toast.custom(ToastrErrorComponent, {
                componentProps: {
                  message: `Un problème est survenu lors de la récupération d'une phase, contactez le support.`,
                  id: toastrId,
                },
                id: toastrId,
              });
            },
          }),
        ),
      ),
      supprimer: rxMethod<{ idPhase: string; next: () => void }>(
        pipe(
          switchMap(({ idPhase, next }) =>
            phaseService.supprimer(idPhase).pipe(
              tapResponse({
                next: () => {
                  store.supprimerPhase(idPhase);
                  next();
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression d'une phase, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
